import axios from 'axios';
import { apiGet } from './api';
import type { ParametriCautare, RaspunsCautare, RezultatCautare } from '@/types';

// ============================================================================
// Căutarea în Dosarul Investiției — un singur apel, către un singur endpoint.
//
// De ce s-a aruncat tot ce era înainte în fișierul acesta:
//   - `globalSearch`/`searchWorkPackages` interogau `/work_packages` (OpenProject).
//     Tabela `work_packages` are 0 rânduri în producție — de aceea căutarea
//     „nu găsea nimic". Nu era o eroare de filtrare, era o sursă goală.
//   - `searchProjects` cerea `/projects` cu filtre HAL; drepturile ajungeau să
//     depindă de API-ul OpenProject, nu de regula casei.
//   - `highlightSearchTerm` întorcea un șir HTML (`<mark>`), care ar fi cerut
//     `dangerouslySetInnerHTML` peste nume de fișiere introduse de utilizatori.
//     Evidențierea se face acum din offset-urile trimise de backend, ca noduri
//     React (vezi `EvidentiazaFragment`).
//
// Drepturile NU se verifică aici. Backendul le aplică în interogare (CTE-ul
// `proiecte_vizibile`, copiat din GET /auth/data/projects); orice rând sosit
// aparține deja unui proiect vizibil utilizatorului.
// ============================================================================

// Sub două caractere nu se cere nimic: backendul întoarce oricum listă goală,
// iar o cerere pe fiecare tastă apăsată nu ajută pe nimeni.
export const CAUTARE_MIN_CARACTERE = 2;

// Limita backendului. Peste ea, răspunsul vine cu `trunchiat: true`.
export const CAUTARE_LIMITA_MAXIMA = 50;

// `apiGet` trimite direct prefixele /auth/ (api.ts), fără maparea și fallback-ul
// către API-ul OpenProject. Aici asta contează: un fallback ar ocoli tocmai
// regula de acces pentru care există endpointul.
export async function cautaInDosare(
  parametri: ParametriCautare,
  semnal?: AbortSignal
): Promise<RaspunsCautare> {
  const params: Record<string, string | number> = { q: parametri.q };
  if (parametri.tip) params.tip = parametri.tip;
  if (parametri.proiect) params.proiect = parametri.proiect;
  if (parametri.limita) params.limita = parametri.limita;

  const raspuns = await apiGet<RaspunsCautare>('/auth/data/search', {
    params,
    signal: semnal,
  });

  return normalizeazaRaspuns(raspuns, parametri.q);
}

// Plasă de siguranță: un răspuns fără `rezultate` (proxy, eroare intermediară)
// nu trebuie să pice pe `.map`. Rândurile fără proiect se aruncă — un rezultat
// care nu se poate urmări până la un proiect n-are ce căuta pe ecran, indiferent
// de ce l-a produs.
function normalizeazaRaspuns(raspuns: RaspunsCautare, q: string): RaspunsCautare {
  const rezultate = Array.isArray(raspuns?.rezultate)
    ? raspuns.rezultate.filter((r): r is RezultatCautare => !!r && !!r.proiect && !!r.cheie)
    : [];
  return {
    interogare: typeof raspuns?.interogare === 'string' ? raspuns.interogare : q,
    jetoane: Array.isArray(raspuns?.jetoane) ? raspuns.jetoane : [],
    tipIntuit: raspuns?.tipIntuit ?? null,
    total: typeof raspuns?.total === 'number' ? raspuns.total : rezultate.length,
    rezultate,
    acoperire: Array.isArray(raspuns?.acoperire) ? raspuns.acoperire : [],
    trunchiat: raspuns?.trunchiat === true,
  };
}

// O cerere anulată de AbortController NU e o eroare: la fiecare tastă apăsată
// anulăm cererea anterioară, iar un rând roșu la fiecare literă ar fi absurd.
export function esteAnulare(eroare: unknown): boolean {
  if (axios.isCancel(eroare)) return true;
  const e = eroare as { code?: string; name?: string } | null;
  return e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError' || e?.name === 'AbortError';
}

// Mesajul de eroare. `apiGet` folosește axios „gol" pentru prefixele /auth/,
// deci interceptorul care traduce erorile (instalat pe apiClient) NU rulează:
// aici sosește un AxiosError brut, nu un ApiError. Mesajul serverului e deja în
// română („Căutarea nu a putut fi efectuată."), așa că se preferă când există.
export function mesajEroareCautare(eroare: unknown): string {
  const e = eroare as {
    response?: { status?: number; data?: { message?: unknown; error?: unknown } };
  } | null;
  const mesajServer = e?.response?.data?.message;
  if (typeof mesajServer === 'string' && mesajServer.trim()) return mesajServer;
  if (e?.response?.status === 401 || e?.response?.status === 403) {
    return 'Sesiunea nu mai este validă. Reautentificați-vă și încercați din nou.';
  }
  return 'Căutarea nu a putut fi efectuată. Încercați din nou.';
}
